import React from "react";
import { Truck, Snowflake, Zap, Package, Radio, Globe2, Users } from "lucide-react";
import { useLang, PARTNERS } from "@/lib/i18n";
import { Reveal, CountUp, SectionLabel } from "@/lib/ui";

/* ---------- Editorial Marquee ---------- */
const MARQUEE = [
  { ro: "TRANSPORT EUROPEAN", en: "EUROPEAN TRANSPORT" },
  { ro: "MONITORIZARE GPS 24/7", en: "24/7 GPS TRACKING" },
  { ro: "LOGISTICĂ B2B", en: "B2B LOGISTICS" },
  { ro: "NORMĂ EURO 6", en: "EURO 6 STANDARD" },
  { ro: "ASIGURARE CMR", en: "CMR INSURANCE" },
  { ro: "PUNCTUALITATE", en: "PUNCTUALITY" },
  { ro: "27 ȚĂRI UE", en: "27 EU COUNTRIES" },
];
export function Marquee() {
  const { t } = useLang();
  const row = [...MARQUEE, ...MARQUEE];
  return (
    <section className="relative border-y border-white/10 bg-slate-950 py-6 overflow-hidden">
      <div className="flex whitespace-nowrap" style={{ animation: "marquee 20s linear infinite" }}>
        {row.map((w, i) => (
          <span key={i} className="font-marquee text-2xl md:text-4xl font-semibold uppercase tracking-wide text-transparent px-8 flex items-center gap-8"
            style={{ WebkitTextStroke: "1px rgba(148,163,184,0.35)" }}>
            {t(w)}<span className="text-sky-500/70" style={{ WebkitTextStroke: "0" }}>◆</span>
          </span>
        ))}
      </div>
      <style>{`@keyframes marquee{from{transform:translateX(0)}to{transform:translateX(-50%)}}`}</style>
    </section>
  );
}

/* ---------- Partners ---------- */
export function Partners() {
  const { t } = useLang();
  return (
    <section className="relative py-20 md:py-24 bg-slate-950 border-t border-white/5">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <SectionLabel>{t({ ro: "Parteneriate de succes", en: "Successful partnerships" })}</SectionLabel>
          <h2 className="mt-5 font-display text-3xl md:text-4xl font-extrabold tracking-tighter">
            {t({ ro: "Colaborăm cu nume de referință", en: "We work with leading names" })}
          </h2>
          <p className="mt-4 text-slate-400">
            {t({ ro: "Fiecare kilometru parcurs este o dovadă de respect reciproc și succes comun.", en: "Every kilometer traveled is proof of mutual respect and shared success." })}
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
          {PARTNERS.map((p, i) => {
            const isLastOdd = PARTNERS.length % 2 !== 0 && i === PARTNERS.length - 1;
            return (
              <Reveal
                key={p.name}
                delay={i * 0.05}
                className={isLastOdd ? "col-span-2 sm:col-span-1 flex justify-center" : ""}
              >
                <div
                  data-testid={`partner-${p.name.toLowerCase()}`}
                  className={`group grid h-24 place-items-center rounded-2xl border border-white/10 bg-white px-5 hover:-translate-y-1 transition-transform duration-300 ${
                    isLastOdd ? "w-[calc(50%-0.5rem)] sm:w-full" : ""
                  }`}
                >
                  <img src={p.img} alt={p.label} className="max-h-10 w-auto max-w-full object-contain" />
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Stats ---------- */
const STATS = [
  { n: 5, suf: "+", ro: "Ani de experiență", en: "Years of experience" },
  { n: 3000, suf: "+", ro: "Transporturi livrate", en: "Shipments delivered" },
  { n: 24, suf: "/7", ro: "Suport dedicat", en: "Dedicated support" },
  { n: 27, suf: "", ro: "Țări europene", en: "European countries" },
];
export function Stats() {
  const { t } = useLang();
  return (
    <section className="relative bg-mesh py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-white/10">
        {STATS.map((s, i) => (
          <Reveal key={i} delay={i * 0.08} className="px-6 py-6 lg:py-0 text-center lg:text-left">
            <div className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter text-white">
              <CountUp to={s.n} /><span className="text-sky-400">{s.suf}</span>
            </div>
            <div className="mt-3 text-sm text-slate-400 font-medium">{t(s)}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------- About / Numbered Manifesto ---------- */
const CHAPTERS = [
  { ro: "Cuvântul dat este literă de lege. Fiecare marfă încredințată reprezintă timpul și efortul partenerilor noștri.", en: "A given word is law. Every entrusted shipment represents the time and effort of our partners." },
  { ro: "Am extins viziunea locală din Bucovina la scară europeană, investind responsabilitate în fiecare cursă.", en: "We scaled our local Bukovina vision to a European level, investing responsibility into every route." },
  { ro: "Nu privim transportul ca pe o simplă deplasare de bunuri, ci ca pe o sincronizare între tehnologie și încredere.", en: "We don't see transport as a mere movement of goods, but as a sync between technology and trust." },
];
export function About() {
  const { t } = useLang();
  return (
    <section id="despre" className="relative py-24 md:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionLabel>{t({ ro: "Despre noi", en: "About us" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]">
              {t({ ro: "Din Suceava, pentru ", en: "From Suceava, for the " })}
              <span className="text-gradient">{t({ ro: "întreaga Europă", en: "whole of Europe" })}</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-6">
            {CHAPTERS.map((c, i) => (
              <Reveal key={i} delay={i * 0.1} className="flex gap-5 group">
                <span className="font-display text-sky-400/80 text-lg font-bold tabular-nums pt-1">0{i + 1}</span>
                <p className="text-slate-300 text-lg leading-relaxed border-l border-white/10 pl-5 group-hover:border-sky-400/50 transition-colors">
                  {t(c)}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15} className="relative">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <img src="/poza7.webp" alt="" className="w-full aspect-[3/4] object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 glass rounded-2xl p-5 flex items-center gap-4">
              <div className="grid h-12 w-12 place-items-center rounded-xl bg-blue-600 shrink-0"><Truck className="h-6 w-6 text-white" /></div>
              <div>
                <div className="font-display text-2xl font-bold">HEB TEAM TRANS</div>
                <div className="text-sm text-slate-400">{t({ ro: "„La timp, întotdeauna, acolo unde ai nevoie.”", en: "\"On time, always, where you need it.\"" })}</div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- Services ---------- */
const SRV_POINTS = [
  { icon: Globe2, ro: "Transport național", en: "National transport" },
  { icon: Radio, ro: "Transport internațional", en: "International transport" },
  { icon: Truck, ro: "Tehnologie avansată", en: "Advanced technology" },
  { icon: Users, ro: "Responsabilitate deplină", en: "Full responsibility" },
];
export function Services() {
  const { t } = useLang();
  return (
    <section id="servicii" className="relative py-24 md:py-32 bg-mesh overflow-hidden">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal className="relative order-2 lg:order-1">
          <div className="relative overflow-hidden rounded-[2rem] border border-white/10">
            <img src="/poza8.webp" alt="" className="w-full aspect-[3/4] object-cover transition-transform duration-700 hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 grid grid-cols-2 gap-3">
              {SRV_POINTS.map((p, i) => {
                const Icon = p.icon;
                return (
                  <div key={i} className="flex items-center gap-2.5 glass rounded-xl px-3 py-2.5">
                    <Icon className="h-4 w-4 text-sky-400 shrink-0" />
                    <span className="text-xs font-semibold">{t(p)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <SectionLabel>{t({ ro: "Serviciile noastre", en: "Our services" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]">
              {t({ ro: "Transport rutier ", en: "National & international " })}
              <span className="text-gradient">{t({ ro: "național și internațional de mărfuri", en: "road freight transport" })}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-300 text-lg leading-relaxed">
              {t({
                ro: "Asigurăm legături logistice strategice între România și piețele europene, având ca punct de pornire regiunea Bucovinei. Performanța noastră se bazează pe punctualitate strictă și pe o capacitate ridicată de adaptare la cerințele specifice ale fiecărui partener.",
                en: "We provide strategic logistics links between Romania and European markets, starting from the Bukovina region. Our performance is built on strict punctuality and a high capacity to adapt to each partner's specific requirements.",
              })}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-5 text-slate-400 text-lg leading-relaxed border-l-2 border-blue-600/60 pl-5">
              {t({
                ro: "Transformăm transportul rutier dintr-o simplă livrare într-un proces integrat, susținut de tehnologie avansată, expertiză solidă și responsabilitate deplină.",
                en: "We turn road transport from a simple delivery into an integrated process, supported by advanced technology, solid expertise and full responsibility.",
              })}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------- Why Us ---------- */
const WHY = [
  { icon: ShieldIcon, ro: "Integritate garantată", en: "Guaranteed integrity", dro: "Tratăm fiecare încărcătură cu responsabilitate și ne asigurăm că ajunge intactă.", den: "We handle every shipment responsibly and ensure it arrives intact." },
  { icon: Radio, ro: "Transparență totală", en: "Full transparency", dro: "Monitorizare GPS în timp real și informații clare despre stadiul transportului tău.", den: "Real-time GPS tracking and clear information about your shipment status." },
  { icon: Globe2, ro: "Acoperire europeană", en: "European coverage", dro: "Rute frecvente în 27 de țări din Uniunea Europeană, fără frontiere.", den: "Frequent routes across 27 EU countries, without borders." },
  { icon: Users, ro: "Echipă dedicată", en: "Dedicated team", dro: "Comunicare directă și umană, adaptată cerințelor specifice ale afacerii tale.", den: "Direct, human communication tailored to your business needs." },
];
function ShieldIcon(props){ return <Zap {...props} />; }
export function WhyUs() {
  const { t } = useLang();
  return (
    <section id="de-ce-noi" className="relative py-24 md:py-32 bg-slate-950">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="max-w-2xl">
          <SectionLabel>{t({ ro: "De ce HEB TEAM TRANS", en: "Why HEB TEAM TRANS" })}</SectionLabel>
          <Reveal>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              {t({ ro: "Partenerul tău pentru transport european", en: "Your partner for European transport" })}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-slate-400 text-lg">
              {t({ ro: "Performanța noastră nu se măsoară doar în kilometri, ci în standardele aplicate riguros la fiecare transport.", en: "Our performance is measured not only in kilometers, but in the standards applied rigorously to every shipment." })}
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {WHY.map((w, i) => {
            const Icon = w.icon;
            return (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`why-card-${i}`} className="group h-full rounded-2xl border border-white/10 bg-slate-900/50 p-7 hover:-translate-y-1 hover:border-sky-400/40 transition-[transform,border-color] duration-300">
                  <div className="grid h-14 w-14 place-items-center rounded-xl bg-blue-600/15 border border-blue-500/20 group-hover:bg-blue-600 transition-colors">
                    <Icon className="h-6 w-6 text-sky-400 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold">{t(w)}</h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">{t({ ro: w.dro, en: w.den })}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
